import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RichContent from "@/components/RichContent";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, FileText, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const resolveImg = (src) => (src?.startsWith("http") ? src : `${API}/files/${src}`);
const APPLY_URL = "https://altezamarketplace.com/sell";

export default function ResourceArticle() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const preview = searchParams.get("preview") === "1";
  const { user, loading: authLoading } = useAuth();

  const [resource, setResource] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { pdf_url }

  useEffect(() => {
    // For preview mode, wait until auth has resolved so admins hit the preview endpoint
    if (preview && authLoading) return;
    setNotFound(false);
    const url = preview && user?.role === "admin"
      ? `/admin/resources/preview/${slug}`
      : `/resources/${slug}`;
    api.get(url)
      .then(({ data }) => setResource(data))
      .catch(() => setNotFound(true));
  }, [slug, preview, user, authLoading]);

  const triggerDownload = (pdfUrl) => {
    if (!pdfUrl) return;
    const base = API.replace(/\/api$/, "");
    const href = pdfUrl.startsWith("http") ? pdfUrl : `${base}${pdfUrl}`;
    const a = document.createElement("a");
    a.href = href;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/resources/${resource.id}/download`, {
        first_name: form.first_name.trim(),
        email: form.email.trim(),
      });
      setDone({ pdf_url: data.pdf_url });
      if (data.pdf_url) setTimeout(() => triggerDownload(data.pdf_url), 400);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const openForm = () => { setDone(null); setForm({ first_name: "", email: "" }); setOpen(true); };

  if (notFound)
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-serif text-3xl">Resource not found</h1>
        <Link to="/resources" className="mt-4 text-primary hover:underline">← Back to Seller Resources</Link>
      </div>
    );
  if (!resource)
    return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading…</div>;

  const hasPdf = !!resource.pdf_path;
  const dlHeading = resource.download_heading || "Want the printable version?";
  const dlBlurb = resource.download_blurb || `Download the free ${resource.title} and work through your plan step by step.`;
  const dlButton = resource.download_button_label || "Get the Free Checklist";

  return (
    <div data-testid="resource-article-page" className="max-w-3xl mx-auto px-6 py-20 lg:py-28">
      {preview && (
        <div className="mb-6 text-xs tracking-wider uppercase bg-primary/10 text-primary border border-primary/30 rounded-sm px-3 py-2 inline-block">
          Preview — status: {resource.status}
        </div>
      )}
      <Link to="/resources" className="text-sm text-muted-foreground hover:text-primary">← Seller Resources</Link>
      <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mt-8 mb-4">◦ Seller Resources</div>
      <h1 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight">{resource.title}</h1>
      {resource.excerpt && <p className="mt-5 text-lg text-muted-foreground leading-relaxed">{resource.excerpt}</p>}

      {resource.cover_image && (
        <img src={resolveImg(resource.cover_image)} alt={resource.title}
             className="mt-10 w-full rounded-sm border border-border object-cover" />
      )}

      <article className="mt-12">
        <RichContent content={resource.content} />
      </article>

      {/* Free resource download block */}
      {hasPdf && (
        <div data-testid="resource-download-block" className="mt-16 border border-border rounded-sm p-8 lg:p-10 bg-secondary/40">
          <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ Free resource</div>
          <h2 className="font-serif text-2xl lg:text-3xl">{dlHeading}</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">{dlBlurb}</p>
          <button onClick={openForm} data-testid="open-download-form-btn"
                  className="btn-primary mt-6 inline-flex items-center gap-2 px-6 py-3.5 text-sm rounded-sm">
            <Download className="w-4 h-4" /> {dlButton}
          </button>
        </div>
      )}

      {/* Apply to Sell CTA */}
      <div data-testid="apply-to-sell-cta" className="mt-12 border border-border rounded-sm p-8 lg:p-10 text-center">
        <h2 className="font-serif text-2xl lg:text-3xl">Ready to grow your handmade brand with Alteza?</h2>
        <p className="mt-3 text-muted-foreground leading-relaxed max-w-xl mx-auto">
          Alteza gives handmade and independent brands another place to be discovered — without listing fees or monthly seller fees.
        </p>
        <a href={APPLY_URL} data-testid="apply-to-sell-btn"
           className="btn-primary mt-6 inline-flex items-center gap-2 px-6 py-3.5 text-sm rounded-sm">
          Apply to Sell on Alteza <ArrowRight className="w-4 h-4" />
        </a>
      </div>

      {/* Download form / success dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="download-dialog" className="sm:max-w-md">
          {!done ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{dlHeading}</DialogTitle>
                <DialogDescription>Enter your details and we'll send you straight to the download.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-5 mt-2">
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">First name</label>
                  <input data-testid="download-first-name" required value={form.first_name}
                         onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                         className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Email address</label>
                  <input data-testid="download-email" type="email" required value={form.email}
                         onChange={(e) => setForm({ ...form, email: e.target.value })}
                         className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
                </div>
                <button data-testid="submit-download-btn" disabled={submitting}
                        className="btn-primary w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm rounded-sm disabled:opacity-60">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Get My Free Checklist
                </button>
                <p className="text-[11px] text-muted-foreground text-center">No account or password needed. We'll email you handmade-seller tips now and then.</p>
              </form>
            </>
          ) : (
            <div className="text-center py-2" data-testid="download-success">
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
              <h3 className="font-serif text-2xl mt-4">Your resource is ready!</h3>
              <p className="text-sm text-muted-foreground mt-2">Your download should start automatically. If it doesn't, use the button below.</p>
              {done.pdf_url && (
                <button onClick={() => triggerDownload(done.pdf_url)} data-testid="download-resource-btn"
                        className="btn-primary mt-6 inline-flex items-center gap-2 px-6 py-3.5 text-sm rounded-sm">
                  <FileText className="w-4 h-4" /> Download Your Resource
                </button>
              )}
              <a href={APPLY_URL} className="block mt-5 text-sm text-primary hover:underline">
                Ready to sell? Apply to Sell on Alteza →
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
